// deps
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef } from "react";

// internal-deps
import {
    AddCakeMessagePill,
    CartAddItems,
    CartPMMWidget,
    CutleryFTUENudge,
    MAGIC_ITEMS_TYPE,
} from "@swiggy-private/cart-ui";
import { useSessionStorage, isJioHotstarFlow } from "@swiggy-private/checkout-core";
import {
    CartItemWidgetReposition,
    CartItemWidgetRepositionType,
    ValueDrivenCrossSellXP,
} from "@swiggy-private/cart-types";

// components
import { RxSpecialInstructions } from "@cart-client/components/rx-special-instructions";
import { CartRestaurantSuggestionText, CartMessagePersonalised } from "@swiggy-private/cart-ui";
import { WRITE_INSTRUCTIONS_FOR_REST } from "@cart-client/components/cart-renderer/constants";
import CutleryOptOut from "@cart-client/components/cutlery-opt-out";

// helpers
import { getCartItemWidgetReposition, isCartContainBoltItems } from "@cart-client/helpers/Cart";
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { isEmpty, throttle } from "@swiggy-private/js-utils";
import { getApplyCouponWidgetData } from "@cart-client/helpers/Coupon";

// context
import { CookingInstructionsFocusContext } from "@cart-client/contexts/CookingInstructionsFocusContext";

// hooks
import { useRxInstruction } from "@cart-client/hooks/useRxInstructions";
import { useCutleryOptInOut } from "@cart-client/hooks/useCutleryOptInOut";
import { useAddCakeMessage } from "@cart-client/hooks/useAddCakeMessage/useAddCakeMessage";

// constants
import { AUTO_ADD_OR_ONE_TAP_PLAN_TYPE_SESSION_KEY } from "@cart-client/constants/StorageKeys";

// types
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";

// styles
import { CartItemActionsContainer, CartItemRelativeContainer, ContainerStyled } from "./CartItemActions.styled";
import { isAipFlow } from "@cart-client/helpers/QuickCheckoutCart";

export interface CartItemActionsProps {
    disableRedirectionToMenu: boolean;
    specialInstruction: string;
    isCroutonVisible: boolean;
    isBulkOrderFn: boolean;
    cartResponse: ReviewedCart | GiftingReviewedCart | null;
    goToMenu: (restaurantId?: string | number) => void;
    isPopCart?: boolean;
    isIrctcCartSession?: boolean;
    restaurantId?: string | number;
}

const Container = memo(
    React.forwardRef<HTMLDivElement, { isContentEmpty?: boolean; children: React.ReactNode }>((props, ref) => {
        return (
            <CartItemRelativeContainer isContentEmpty={props.isContentEmpty} ref={ref}>
                <ContainerStyled isContentEmpty={props.isContentEmpty}>{props.children}</ContainerStyled>
            </CartItemRelativeContainer>
        );
    }),
);

Container.displayName = "Container";

function CartItemActions({
    disableRedirectionToMenu,
    specialInstruction,
    isCroutonVisible,
    isBulkOrderFn,
    cartResponse,
    goToMenu,
    isPopCart,
    isIrctcCartSession,
    restaurantId,
}: CartItemActionsProps) {
    const { setCookingInstructionsFocused, scrollCookingInstructionsOnClick } = useContext(
        CookingInstructionsFocusContext,
    );
    const [autoAddPlan] = useSessionStorage(AUTO_ADD_OR_ONE_TAP_PLAN_TYPE_SESSION_KEY, "");
    const { onImpressionAddMoreAndSaveMore } = useCrossSellAnalytics(cartResponse);
    const { valueDrivenCrossSellXPVariant } = useCartRendererXP(cartResponse);
    const {
        isEditState,
        handleAddCakeMessageClick,
        showAddCakeMessagePill,
        PMMBanner,
        isScrolled,
        setIsScrolled,
        cakeMessageHalfCardState,
        setCakeMessageHalfCardState,
        predefinedWordsForCakeMessage,
        maxCharactersPerCakeMessage,
        filterCakeItems,
        submitCakeMessage,
        onHalfCardImpression,
        onHalfCardClick,
        onAddCakeMessagePillImpression,
        pillText,
        onMessageHalfCardImpression,
        onMessageHalfCardClick,
    } = useAddCakeMessage(restaurantId);

    const showHalfCard =
        cakeMessageHalfCardState.showSelectCakeHalfCard || cakeMessageHalfCardState.showAddCakeMessageHalfCard;

    const containerRef = useRef<HTMLDivElement>(null);
    const cutleryRef = useRef<HTMLDivElement>(null);
    const nudgeRef = useRef<HTMLDivElement>(null);
    const pmmBannerRef = useRef<HTMLButtonElement>(null);
    const {
        showCutleryTile,
        isCutleryOpted,
        toggleCutlery,
        isFtueForCutlery = false,
        nudgeClosed,
        handleNudgeClosed,
    } = useCutleryOptInOut({ restaurantId });

    const useCutleryInstructionProps = useRxInstruction(isCroutonVisible, specialInstruction, restaurantId);
    const { textAreaActive, note } = useCutleryInstructionProps;
    const hasCookingInstruction = !!note;
    const cartItemWidgetReposition = getCartItemWidgetReposition(cartResponse?.data);

    const isContentEmpty = isEmpty(cartItemWidgetReposition);

    const addItemsCouponSavingData = useMemo(() => {
        return valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.ADD_MORE_ITEMS_TEXT
            ? getApplyCouponWidgetData(cartResponse)
            : undefined;
    }, [cartResponse, valueDrivenCrossSellXPVariant]);

    const getPlaceholder = useMemo(() => {
        if (isBulkOrderFn) {
            return WRITE_INSTRUCTIONS_FOR_REST;
        }
    }, [isBulkOrderFn]);

    const isCartBoltEnabled = isCartContainBoltItems(cartResponse?.data);

    // Check if cart is in magic items in cart
    const isMagicItemsCart = useMemo(() => {
        return (
            cartResponse?.data?.cross_sell_widget_data?.presentation_info?.widget_properties_info?.type ===
            MAGIC_ITEMS_TYPE
        );
    }, [cartResponse?.data?.cross_sell_widget_data?.presentation_info?.widget_properties_info?.type]);

    const handleShowNudge = useCallback(() => {
        handleNudgeClosed(false);
    }, [handleNudgeClosed]);

    useEffect(() => {
        handleNudgeClosed(isFtueForCutlery);
    }, [isFtueForCutlery, handleNudgeClosed]);

    // Add this effect to track scrolling

    const handleScroll = useCallback(() => {
        if (containerRef.current) {
            // Check if scrolled horizontally
            const scrollLeft = containerRef.current.scrollLeft;
            setIsScrolled(scrollLeft > 40);
        }
    }, [setIsScrolled]);

    const throttledHandleScroll = useMemo(() => throttle(handleScroll, 100), [handleScroll]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", throttledHandleScroll, { passive: true });

            // Clean up
            return () => {
                container.removeEventListener("scroll", throttledHandleScroll);
            };
        }
    }, [throttledHandleScroll]);

    const goToMenuHandler = useCallback(() => {
        goToMenu(restaurantId?.toString());
    }, [goToMenu, restaurantId]);

    const renderActions = (action?: CartItemWidgetReposition) => {
        switch (action?.type) {
            case CartItemWidgetRepositionType.ADD_MORE_ITEMS:
                if (disableRedirectionToMenu || isCartBoltEnabled) {
                    return null;
                }
                return (
                    <CartAddItems
                        key={action?.type}
                        couponSavingsInfo={addItemsCouponSavingData}
                        autoAddPlan={autoAddPlan}
                        onClick={goToMenuHandler}
                        onImpressionEvent={onImpressionAddMoreAndSaveMore}
                        text={action?.text}
                    />
                );

            case CartItemWidgetRepositionType.ADD_MESSAGE_ON_CAKE:
                if (isPopCart || isIrctcCartSession || !showAddCakeMessagePill) {
                    return null;
                }
                return (
                    <React.Fragment key={action?.type}>
                        <AddCakeMessagePill
                            text={pillText}
                            onClick={handleAddCakeMessageClick}
                            hideAnimation={!isEmpty(PMMBanner) || isEditState}
                            onImpression={onAddCakeMessagePillImpression}
                            isEditState={isEditState}
                            pmmBannerRef={pmmBannerRef}
                        />
                        {showHalfCard && (
                            <CartMessagePersonalised
                                filterCakeItems={filterCakeItems}
                                cakeMessageHalfCardState={cakeMessageHalfCardState}
                                setCakeMessageHalfCardState={setCakeMessageHalfCardState}
                                predefinedWordsForCakeMessage={predefinedWordsForCakeMessage}
                                maxCharactersPerCakeMessage={maxCharactersPerCakeMessage}
                                submitCakeMessage={submitCakeMessage}
                                onHalfCardImpression={onHalfCardImpression}
                                onHalfCardClick={onHalfCardClick}
                                onMessageHalfCardImpression={onMessageHalfCardImpression}
                                onMessageHalfCardClick={onMessageHalfCardClick}
                            />
                        )}
                    </React.Fragment>
                );
            case CartItemWidgetRepositionType.SEND_CUTLERY:
                if (isIrctcCartSession) {
                    return null;
                }
                return (
                    <CutleryOptOut
                        key={action?.type}
                        showCutleryTile={showCutleryTile}
                        isCutleryOpted={isCutleryOpted}
                        toggleCutlery={toggleCutlery}
                        cutleryRef={cutleryRef}
                        title={action?.text}
                    />
                );

            case CartItemWidgetRepositionType.COOKING_INSTRUCTIONS:
                if (isPopCart || isIrctcCartSession) {
                    return null;
                }
                return (
                    <RxSpecialInstructions
                        key={action?.type}
                        specialInstruction={specialInstruction}
                        placeholder={getPlaceholder}
                        title={action?.text}
                        {...useCutleryInstructionProps}
                        onCookingInstructionsFocus={() => setCookingInstructionsFocused(true)}
                        onCookingInstructionsBlur={() => setCookingInstructionsFocused(false)}
                        onCookingInstructionsClick={scrollCookingInstructionsOnClick}
                    />
                );
            default:
                return null;
        }
    };

    const shouldShowCutleryFtueNudge =
        !showAddCakeMessagePill && !isMagicItemsCart && !isJioHotstarFlow() && !isAipFlow();

    return (
        <CartItemActionsContainer>
            <Container isContentEmpty={isContentEmpty} ref={containerRef}>
                {cartItemWidgetReposition?.map(renderActions)}
            </Container>
            {!useCutleryInstructionProps?.isRxInstructionsHalfCardXPEnabled && (
                <CartRestaurantSuggestionText
                    specialInstruction={specialInstruction}
                    {...useCutleryInstructionProps}
                    placeholder={getPlaceholder}
                    onCookingInstructionsFocus={() => setCookingInstructionsFocused(true)}
                    onCookingInstructionsBlur={() => setCookingInstructionsFocused(false)}
                    onCookingInstructionsClick={scrollCookingInstructionsOnClick}
                />
            )}
            {shouldShowCutleryFtueNudge && (
                <CutleryFTUENudge
                    nudgeRef={nudgeRef}
                    showNudge={nudgeClosed && !isContentEmpty}
                    checked={isCutleryOpted}
                    handleShowNudge={handleShowNudge}
                />
            )}
            <CartPMMWidget
                PMMBanner={PMMBanner}
                isEditState={isEditState}
                hideArrow={isScrolled || hasCookingInstruction}
                RxInstructionsTextAreaActive={textAreaActive}
                targetRef={pmmBannerRef}
                isBoltCart={isCartBoltEnabled}
            />
        </CartItemActionsContainer>
    );
}

export default memo(CartItemActions);
